'use client';

import loginContract from '@bells/api/auth/login';
import { type AuthedUser } from '@bells/schema/models/auth';
import { type JwtToken } from '@bells/schema/models/signup';
import Cookies from 'js-cookie';
import ms from 'ms';
import { type PropsWithChildren, useMemo, useState } from 'react';
import { AuthCookieName } from '../auth/auth-constant';
import { generateClient } from '../rest/client';
import { UserContext } from './auth-context';
import { usePathname } from 'next/navigation';
import { useAppSettings } from '../app/app-provider';
const authClient = generateClient(loginContract);

export const AuthProvider = ({
  children,
  user: initUser = undefined,
  jwt,
  secure,
}: PropsWithChildren<{
  user?: AuthedUser;
  jwt?: JwtToken;
  secure?: boolean;
}>) => {
  const [currentUser, setCurrentUser] = useState<AuthedUser | undefined>(
    initUser
  );
  const pathName = usePathname();
  const [isAuthed] = useState<boolean>(jwt?.accessToken ? true : false);
  const [openSettings, setOpenSettings] = useState<boolean>(false);

  const [internalToken, setInternalToken] = useState<string | undefined>(
    jwt?.accessToken
  );

  const { frontendUrl } = useAppSettings();

  const logout = (callback?: string) => {
    setCurrentUser(undefined);
    setInternalToken(undefined);

    Cookies.remove(AuthCookieName, {
      secure,
      sameSite: 'Strict',
    });

    localStorage.removeItem(AuthCookieName);
    localStorage.removeItem(AuthCookieName + 'Timeout');
    if (callback) {
      window.location.href = `${frontendUrl}${callback}`;
    } else {
      if (pathName == callback) {
        window.location.reload();
      } else {
        window.location.href = `${frontendUrl}`;
      }
    }
  };

  const login = (token: string, timeout?: string, callback?: () => void) => {
    setToken(token, timeout);
    callback?.();
  };

  const refresh = async (cb?: () => void) => {
    if (internalToken) {
      const result = await authClient.login({
        body: {
          token: internalToken,
        },
      });

      if (result.status === 200) {
        const { user, jwt } = result.body;
        setToken(jwt.accessToken, jwt.expiresIn);
        setCurrentUser(user);
        setInternalToken(jwt.accessToken);
      }
      cb?.();
    }
  };

  const setToken = (token: string, expiresIn: string = '1year') => {
    const expiresDate = new Date(Date.now() + ms(expiresIn));

    Cookies.set(AuthCookieName, token, {
      sameSite: 'Strict',
      secure,
      expires: expiresDate,
    });
    localStorage.setItem(AuthCookieName, token);
    localStorage.setItem(AuthCookieName + 'Timeout', expiresIn);
  };

  const config = useMemo(() => {
    return {
      openSettings,
      setOpenSettings,
      isAuthed,
      currentUser,
      setCurrentUser,
      accessToken: internalToken,
      setToken,
      logout,
      refresh,
      login,
    };
  }, [
    internalToken,
    openSettings,
    setOpenSettings,
    setToken,
    logout,
    isAuthed,
    currentUser,
    setCurrentUser,
    refresh,
    login,
  ]);

  return <UserContext.Provider value={config}>{children}</UserContext.Provider>;
};
